import classnames from 'classnames';
import dayjs from 'dayjs';
import {
  IconButton,
  Icon,
  Tooltip,
  Snackbar,
  Snack,
  TableCell,
} from '@nike/eds';
import { useEffect, useState } from 'react';
import RankingListDelete from './RankingListDelete';
import RankingListPublish from './RankingListPublish';
import HotsearchClient from '../../clientAPI/hotsearch';

import RankingListEdit from './RankingListEdit';
import RankingListMetaDataAddAndEdit from './RankingListMetaDataAddAndEdit';
import { useLoaderData } from 'react-router-dom';
import { isExistScope } from '../../utils/authHelpers';
import RankingListPreview from './RankingListPreview';

type HotsearchRankingListProps = {
  item: RankingList;
  previewAtom: any;
  editAtom: any;
  deleteRankById: (item: RankingList) => void;
  updateItem: (msg: string) => void;
  afterPublished: () => void;
};

type TimeStatus = 'expired' | 'active' | 'upcoming';

export default function HotsearchRankingDetail(
  props: HotsearchRankingListProps,
) {
  const { item, previewAtom, editAtom, deleteRankById } = props;
  const [open, setOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(false);

  const [enable, setEnable] = useState(item.enable);

  useEffect(() => {
    setEnable(item.enable);
  }, [item]);

  const [metaMode, setMetaMode] = useState<Mode>('ADD');

  const [errorMsg, setErrorMsg] = useState('');
  const [showSnack, setShowSnack] = useState(false);
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canEditMeta = isExistScope('search360:hotsearch:meta-update', jwt);
  const canEditContent = isExistScope(
    'search360:hotsearch:content-update',
    jwt,
  );
  const canDeleteRank = isExistScope('search360:hotsearch:delete', jwt);
  const canPublishRank = isExistScope('search360:hotsearch:publish', jwt);

  let timeStatus: TimeStatus = 'active';
  if (dayjs().isBefore(dayjs(item.startTime))) {
    timeStatus = 'upcoming';
  }
  if (dayjs().isAfter(dayjs(item.endTime))) {
    timeStatus = 'expired';
  }
  const onDismiss = () => {
    setShowContentModal(false);
    setShowMetaModal(false);
    setShowPreviewModal(false);
    setShowPublishModal(false);
    setEnable(props.item.enable);
  };
  const updateItem = (msg: string) => {
    if (msg) {
      props.updateItem(msg);
    }
  };

  const handleClick = () => {
    setOpen(true);
    setEnable(true);
  };
  const handlePublishClick = () => {
    setSubmitStatus(false);
    setPublishOpen(true);
    setShowPublishModal(true);
    setEnable(true);
  };

  const publishRankById = async (item: RankingList): Promise<void> => {
    console.log('start publish');
    setSubmitStatus(true);
    try {
      const response: Record<string, any> = await HotsearchClient.publishRank(
        item._id,
      );
      if (!response.ok) {
        setSubmitStatus(false);
        setShowSnack(true);
        const json: Record<string, any> = await response.json();
        setErrorMsg(json['message']);
        return;
      } else {
        setPublishOpen(false);
        setShowPublishModal(false);
        props.afterPublished();
      }
      return response.ok;
    } catch (errors) {
      setSubmitStatus(false);
      setShowSnack(true);
      setErrorMsg('error 500');
    }
  };

  const closeDeleteModal = () => {
    setOpen(false);
    setPublishOpen(false);
    setEnable(item.enable);
  };

  const closePublishModal = () => {
    setOpen(false);
    setPublishOpen(false);
    setShowPublishModal(false);
    setEnable(item.enable);
  };

  const deleteRankByIdProps = () => {
    deleteRankById(item);
    setOpen(false);
  };

  const publishRankByIdProps = () => {
    return publishRankById(item);
  };

  const startDate = dayjs(item.startTime).format('YYYY-M-D HH:mm');
  const endDate = dayjs(item.endTime).format('YYYY-M-D HH:mm');

  const channelOptions = [
    { value: '82a74ac1-c527-4470-b7b0-fb5f3ef3c2e2', label: 'Nike App' },
    { value: 'd9a5bc42-4b9c-4976-858a-f159cf99c647', label: 'Nike.COM' },
    { value: '15d92135-394e-487a-8afa-f6c8525c9ea9', label: 'WeChat' },
    { value: '008be467-6c78-4079-94f0-70e2d6cc4003', label: 'SNKRS' },
  ];

  const choosedChannels = item.channelIds.map((channelId, index) => {
    return channelOptions.find((v) => v.value === channelId)?.label;
  });

  const rankEnable = !(timeStatus == 'expired' || !item.enable);

  item.concepts.sort((a, b) => a.name.length - b.name.length);

  const rows = [
    [
      <TableCell key={crypto.randomUUID()}>{item.order}</TableCell>,

      <TableCell key={crypto.randomUUID()}>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <span className='tw-font-bold tw-text-lg'>{item.title}</span>
          <span
            className='tw-text-sm'
            style={{
              color: '#525b75',
              fontWeight: '600',
            }}
          >
            {item.name}
          </span>
        </div>
      </TableCell>,

      // type and status
      <TableCell key={crypto.randomUUID()}>
        <div className='tw-flex tw-flex-col'>
          <span
            className={classnames(
              'tw-inline-block tw-rounded tw-text-xs tw-font-bold tw-transition-all tw-cursor-pointer tw-px-2 tw-py-1',
              {
                'tw-text-blue-600 tw-bg-blue-200': item.type === 'GENERIC',
                'tw-text-green-600 tw-bg-green-200': item.type === 'CUSTOMIZE',
              },
            )}
          >
            {item.type === 'GENERIC' ? 'PRODUCT' : 'HOT SEARCH'}
          </span>
          <span
            className={classnames(
              'tw-inline-block tw-rounded tw-text-xs tw-mt-2 tw-font-bold tw-px-2 tw-py-0.5 tw-mr-3',
              {
                'tw-text-emerald-200 tw-bg-emerald-500':
                  item.status === 'PUBLISHED',
                'tw-text-gray-200 tw-bg-yellow-500': item.status === 'DRAFT',
                'tw-text-gray-200 tw-bg-gray-400': item.status === 'INIT',
              },
            )}
          >
            {item.status === 'INIT' ? 'CREATED' : item.status}
          </span>
        </div>
      </TableCell>,
      // type and status
      <TableCell key={crypto.randomUUID()}>
        <div className='tw-flex tw-flex-col'>
          <span
            className={classnames(
              'tw-inline-block tw-rounded tw-text-xs tw-mt-2 tw-font-bold tw-px-2 tw-py-0.5 tw-mr-3',
              {
                'tw-text-emerald-200 tw-bg-emerald-500': rankEnable,
                'tw-text-gray-200 tw-bg-gray-400': !rankEnable,
              },
            )}
          >
            {rankEnable ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
      </TableCell>,

      // effect  start date and end date
      <TableCell key={crypto.randomUUID()}>
        <Tooltip
          bodySlot={<p className='tw-border tw-px-1'>{timeStatus}</p>}
          enableFocus
        >
          <div className='tw-flex tw-flex-col tw-gap-2 tw-cursor-pointer'>
            <p
              className={classnames('tw-text-sm  tw-font-bold', {
                'tw-text-green-800': timeStatus === 'active',
                'tw-text-red-800': timeStatus === 'expired',
                'tw-text-blue-800': timeStatus === 'upcoming',
              })}
            >
              {dayjs(item.startTime).format('YYYY-MM-DD HH:mm')}
            </p>
            <p
              className={classnames('tw-text-sm  tw-font-bold  tw-mt-2', {
                'tw-text-green-800': timeStatus === 'active',
                'tw-text-red-800': timeStatus === 'expired',
                'tw-text-blue-800': timeStatus === 'upcoming',
              })}
            >
              {dayjs(item.endTime).format('YYYY-MM-DD HH:mm')}
            </p>
            {/* <p>{timeStatus}</p> */}
          </div>
        </Tooltip>
      </TableCell>,

      //Concepts
      <TableCell key={crypto.randomUUID()}>
        <div>
          {item.concepts &&
            item.concepts.map((c) => {
              const ckey = `${item._id}_${c.id}`;
              return (
                <div key={ckey}>
                  <span
                    className='tw-rounded tw-text-xs tw-font-bold  tw-border-sky-900
                       tw-border tw-border-solid tw-text-gray-600 tw-py-[2px] tw-px-1'
                  >
                    {c.name}
                  </span>
                </div>
              );
            })}
        </div>
      </TableCell>,

      <TableCell key={crypto.randomUUID()}>
        <div className='tw-flex tw-flex-col'>
          {choosedChannels.map((e) => {
            return <span key={crypto.randomUUID()}>{e}</span>;
          })}
        </div>
      </TableCell>,

      <TableCell key={crypto.randomUUID()}>
        <div className='tw-flex-1'>
          <div className='tw-flex tw-justify-left tw-items-center tw-mb-6'>
            {/* Edit Metadate start */}
            <div className='tw-mr-6 '>
              <Tooltip bodySlot='Edit Meta' enableFocus>
                <IconButton
                  // icon={<Icon name='Settings' size='m' />}
                  icon={<EditMetaSvgIcon />}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  onClick={() => {
                    setShowMetaModal(true);
                    setMetaMode('EDIT');
                    if (!item.enable) {
                      setEnable(true);
                    }
                  }}
                  label='Edit'
                />
              </Tooltip>
            </div>
            {/* )} */}
            {/* Edit Metadate end */}

            {/* Edit content start */}
            <div className='tw-mr-6 '>
              <Tooltip bodySlot='Edit Content' enableFocus>
                <IconButton
                  icon={<Icon name='Edit' size='m' />}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  onClick={() => {
                    setShowContentModal(true);
                    setEnable(true);
                  }}
                  label='Edit'
                />
              </Tooltip>
            </div>
            {/* Edit Detail end */}

            {/* preview start */}
            <div className='tw-mr-6'>
              <Tooltip bodySlot='Preview' enableFocus>
                <IconButton
                  icon={<Icon name='Show' size='m' />}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  onClick={() => {
                    setShowPreviewModal(true);
                    setEnable(true);
                  }}
                  label='Preview'
                />
              </Tooltip>
            </div>
            {/* preview end */}

            {/* publish start */}
            <div className='tw-mr-6 tw-relative' onClick={handlePublishClick}>
              <Tooltip bodySlot='Publish' enableFocus>
                <IconButton
                  //icon={<Icon name='Check' size='m' />}
                  icon={<PublishSvgIcon />}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  disabled={!(canPublishRank && item.status === 'DRAFT')}
                  label='Publish'
                />
              </Tooltip>
            </div>
            {/* publish end */}

            {/* delete start */}
            <div className='tw-mr-6 tw-relative ' onClick={handleClick}>
              <Tooltip bodySlot='Delete' enableFocus>
                <IconButton
                  icon={<Icon name='Delete' size='m' />}
                  style={{ color: 'var(--eds-color-red-50)' }}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  label='Delete'
                  disabled={!canDeleteRank}
                />
              </Tooltip>
            </div>
            {/* delete end */}
          </div>
          <div className='tw-flex tw-justify-left tw-items-center'>
            <span className='tw-text-[12px] tw-inline tw-text-gray-600'>
              Last updated at {dayjs(item.updatedAt).format('YYYY-M-D HH:mm')}
              <br />
              by {item.updatedBy}
            </span>
          </div>
          <RankingListDelete
            item={item}
            open={open}
            closeDeleteModal={closeDeleteModal}
            submitDelete={deleteRankByIdProps}
          />
          {showPublishModal && (
            <RankingListPublish
              item={item}
              publishOpen={publishOpen}
              submitStatus={submitStatus}
              closePublishModal={closePublishModal}
              publishRankById={publishRankByIdProps}
            />
          )}
          {showContentModal && (
            <RankingListEdit
              itemId={item._id}
              showModal={showContentModal}
              onDismiss={onDismiss}
              updateItem={updateItem}
              canWrite={canEditContent}
              mode={item.title ? 'EDIT' : 'ADD'}
            />
          )}
          {showMetaModal && (
            <RankingListMetaDataAddAndEdit
              itemId={item._id}
              showModal={showMetaModal}
              onDismiss={onDismiss}
              updateItem={updateItem}
              canWrite={canEditMeta}
              mode={metaMode}
            />
          )}
          {showPreviewModal && (
            <RankingListPreview
              itemId={item._id}
              showModal={showPreviewModal}
              onDismiss={onDismiss}
            />
          )}
          <Snackbar style={{ left: '90%', top: '90%' }}>
            {showSnack && (
              <Snack
                autoDismissDuration={5000}
                id='pw-error-notification'
                onDismiss={(_id) => {
                  setShowSnack(false);
                }}
                status='error'
              >
                {errorMsg ? errorMsg : 'success'}
              </Snack>
            )}
          </Snackbar>
        </div>
      </TableCell>,
    ],
  ];

  return (
    <>
      {rows.map((row) => (
        <tr
          key={crypto.randomUUID()}
          className={classnames(
            enable ? 'tw-bg-white' : 'tw-bg-white tw-opacity-50',
          )}
        >
          {row.map((cell) => {
            return cell;
          })}
        </tr>
      ))}
    </>
  );
}

function PublishSvgIcon() {
  return (
    <svg
      className='icon'
      viewBox='0 0 1024 1024'
      version='1.1'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
    >
      <path d='M0 0h1024v1024H0z' fill='#FFFFFF'></path>
      <path d='M789.333333 853.248h-554.666666a21.333333 21.333333 0 0 1-21.333334-21.333333v-42.666667a21.333333 21.333333 0 0 1 21.333334-21.333333h554.666666a21.333333 21.333333 0 0 1 21.333334 21.333333v42.666667a21.333333 21.333333 0 0 1-21.333334 21.333333z m-170.666666-170.666667h-213.333334a21.333333 21.333333 0 0 1-21.333333-21.333333v-234.666667H273.536a20.864 20.864 0 0 1-14.933333-6.357333l-8.533334-8.533333a21.333333 21.333333 0 0 1 0-30.293334l246.997334-247.04a20.522667 20.522667 0 0 1 29.866666 0l247.04 247.082667a21.333333 21.333333 0 0 1 0 30.293333l-8.533333 8.533334a20.992 20.992 0 0 1-14.933333 6.314666H640v234.666667a21.333333 21.333333 0 0 1-21.333333 21.333333z'></path>
    </svg>
  );
}

function EditMetaSvgIcon() {
  return (
    <svg
      className='icon'
      viewBox='0 0 1024 1024'
      version='1.1'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
    >
      <path d='M212.047621 394.956806l256.040561-1.876537 0.559502 76.347508-256.039368 1.87773zM212.787261 621.996337l510.261847-2.582774 0.386521 76.348701-510.261847 2.582774zM572.698273 369.449977L949.836923-0.001193l53.429417 54.540069L626.126497 423.991239z'></path>
      <path d='M959.560797 1023.98807H20.671841V34.958707h677.676883v76.349894H97.021735V947.638177h786.189169V290.265594h76.349893V1023.98807z'></path>
    </svg>
  );
}
